import { createSHA256 } from 'hash-wasm';
import { MetadataIndex, openDatabase } from './database.ts';
import { CHUNK_SIZE, IdbStore, OpfsStore } from './stores.ts';
import { CacheError } from './types.ts';
import type { BlobStore, CacheEntry, DownloadParams } from './types.ts';

const active = new Map<string, Promise<CacheEntry>>();
const DAY = 86_400_000;
const RESERVE = 32 * 1024 * 1024;
const storage = () => globalThis.navigator?.storage;
const aborted = (signal?: AbortSignal) => {
  if (signal?.aborted) throw new CacheError('aborted');
};
function normalize(error: unknown, signal?: AbortSignal): CacheError {
  if (signal?.aborted || (error instanceof Error && error.name === 'AbortError')) return new CacheError('aborted');
  if (error instanceof CacheError) return error;
  if (error instanceof Error && error.name === 'QuotaExceededError') return new CacheError('quota', 'Game storage quota exceeded', { cause: error });
  return new CacheError(error instanceof TypeError ? 'network' : 'permanent', 'Game cache operation failed', { cause: error });
}

/** Abortable backoff; ordinary timers allow consumers/tests to use fake timers. */
function backoff(ms: number, signal?: AbortSignal): Promise<void> {
  aborted(signal);
  return new Promise((resolve, reject) => {
    const onAbort = () => { clearTimeout(timer); reject(new CacheError('aborted')); };
    const timer = setTimeout(() => { signal?.removeEventListener('abort', onAbort); resolve(); }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

// Race pending URL/fetch/read work against abort even if a custom fetch ignores signal.
async function interruptible<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  aborted(signal);
  if (!signal) return promise;
  let onAbort!: () => void;
  const cancellation = new Promise<never>((_, reject) => {
    onAbort = () => reject(new CacheError('aborted'));
    signal.addEventListener('abort', onAbort, { once: true });
  });
  try { return await Promise.race([promise, cancellation]); }
  finally { signal.removeEventListener('abort', onAbort); }
}

export class GameCache {
  private constructor(private readonly store: BlobStore, private readonly index: MetadataIndex) {}

  static async open(opts: { forceBackend?: 'opfs' | 'idb' } = {}): Promise<GameCache> {
    try {
      const db = await openDatabase();
      let store: BlobStore;
      if (opts.forceBackend === 'idb') store = new IdbStore(db);
      else {
        try {
          if (!storage()?.getDirectory) throw new CacheError('unsupported', 'OPFS unavailable');
          store = await OpfsStore.open();
        } catch (error) {
          if (opts.forceBackend === 'opfs') throw new CacheError('unsupported', 'OPFS unavailable', { cause: error });
          store = new IdbStore(db);
        }
      }
      return new GameCache(store, new MetadataIndex(db));
    } catch (error) {
      if (error instanceof CacheError) throw error;
      throw new CacheError('unsupported', 'Persistent game storage unavailable', { cause: error });
    }
  }

  get(sha256: string): Promise<CacheEntry | null> { return this.index.get(sha256); }
  async has(sha256: string): Promise<boolean> { return (await this.get(sha256))?.state === 'complete'; }
  list(): Promise<CacheEntry[]> { return this.index.list(); }

  async download(params: DownloadParams): Promise<CacheEntry> {
    if (!/^[a-f0-9]{64}$/.test(params.sha256) || !Number.isSafeInteger(params.size) || params.size < 0) {
      throw new CacheError('permanent', 'Expected a lowercase SHA-256 and nonnegative safe integer size');
    }
    const existing = active.get(params.sha256);
    if (existing) {
      await interruptible(existing, params.signal);
      return this.touch(params.sha256, params.gameId);
    }
    const task = this.performDownload(params);
    active.set(params.sha256, task);
    try { return await task; }
    finally { if (active.get(params.sha256) === task) active.delete(params.sha256); }
  }

  private async touch(sha: string, gameId?: string): Promise<CacheEntry> {
    const entry = await this.index.update(sha, entry => {
      entry.lastUsedAt = Date.now();
      if (gameId && !entry.gameIds.includes(gameId)) entry.gameIds.push(gameId);
    });
    if (!entry) throw new CacheError('permanent', 'Game is not cached');
    return entry;
  }

  private async checkSpace(remaining: number, sha: string): Promise<void> {
    const manager = storage();
    if (!manager?.estimate) return;
    let estimate = await manager.estimate();
    if (estimate.quota === undefined) return;
    const needed = remaining + RESERVE;
    if (estimate.quota - (estimate.usage ?? 0) < needed) {
      await this.evict(needed - (estimate.quota - (estimate.usage ?? 0)), { protect: [sha] });
      estimate = await manager.estimate();
      if (estimate.quota !== undefined && estimate.quota - (estimate.usage ?? 0) < needed) throw new CacheError('quota');
    }
  }

  private async performDownload(p: DownloadParams): Promise<CacheEntry> {
    let entry: CacheEntry | null = null;
    try {
      aborted(p.signal);
      entry = await this.get(p.sha256);
      if (entry?.state === 'complete') return await this.touch(p.sha256, p.gameId);
      if (entry && entry.size !== p.size) { await this.store.remove(p.sha256); await this.index.remove(p.sha256); entry = null; }
      const now = Date.now();
      entry ??= { sha256: p.sha256, size: p.size, bytesWritten: 0, state: 'partial', filename: p.filename,
        gameIds: [], pinned: false, lastUsedAt: now, createdAt: now, verifiedAt: null };
      if (!entry.gameIds.includes(p.gameId)) entry.gameIds.push(p.gameId);
      entry.lastUsedAt = now;
      await this.checkSpace(p.size - entry.bytesWritten, p.sha256);
      await this.index.put(entry);
      // Discard any uncheckpointed tail left by a terminated browser session.
      await this.store.truncate(p.sha256, entry.bytesWritten);
      const fetcher = p.fetchImpl ?? globalThis.fetch;
      let refreshed = false;
      const started = performance.now();
      const initialBytes = entry.bytesWritten;
      for (let attempt = 0; attempt < 5 && entry.bytesWritten < p.size; attempt++) {
        try {
          aborted(p.signal);
          let response: Response;
          while (true) {
            const url = await interruptible(p.getUrl(), p.signal);
            response = await interruptible(fetcher(url, { signal: p.signal, headers: entry.bytesWritten ? { Range: `bytes=${entry.bytesWritten}-` } : {} }), p.signal);
            if ((response.status === 401 || response.status === 403) && !refreshed) {
              refreshed = true;
              await response.body?.cancel();
              continue;
            }
            break;
          }
          if (response.status !== 200 && response.status !== 206) {
            await response.body?.cancel();
            throw new CacheError(response.status >= 500 || [408, 429].includes(response.status) ? 'network' : 'permanent', `Download HTTP ${response.status}`);
          }
          if (response.status === 200 && entry.bytesWritten) {
            await this.store.truncate(p.sha256, 0);
            entry.bytesWritten = 0;
            await this.index.put(entry);
            await this.checkSpace(p.size, p.sha256);
          }
          if (response.status === 206) {
            const range = response.headers.get('content-range');
            if (range && !range.startsWith(`bytes ${entry.bytesWritten}-`)) {
              await response.body?.cancel();
              throw new CacheError('permanent', 'Server returned an incorrect byte range');
            }
          }
          if (!response.body) throw new CacheError('network', 'Download body missing');
          const reader = response.body.getReader();
          // Commit bounded batches instead of reopening/copying an OPFS file for
          // every small network packet. Closing each write makes checkpoints durable.
          const buffer = new Uint8Array(CHUNK_SIZE);
          let buffered = 0;
          const flush = async () => {
            if (!buffered) return;
            const length = buffered;
            buffered = 0;
            await this.store.write(p.sha256, entry!.bytesWritten, buffer.subarray(0, length));
            entry!.bytesWritten += length;
            await this.index.put(entry!);
          };
          try {
            while (true) {
              aborted(p.signal);
              const part = await interruptible(reader.read(), p.signal);
              if (part.done) break;
              if (entry.bytesWritten + buffered + part.value.length > p.size) throw new CacheError('integrity', 'Download exceeds expected size');
              for (let offset = 0; offset < part.value.length;) {
                const length = Math.min(CHUNK_SIZE - buffered, part.value.length - offset);
                buffer.set(part.value.subarray(offset, offset + length), buffered);
                buffered += length;
                offset += length;
                if (buffered === CHUNK_SIZE) await flush();
              }
              p.onProgress?.({ loaded: entry.bytesWritten + buffered, total: p.size,
                speedBps: Math.max(0, entry.bytesWritten + buffered - initialBytes) / Math.max((performance.now() - started) / 1000, 0.001) });
            }
          } finally {
            // Do not await cancellation: a broken stream may never settle it.
            void reader.cancel().catch(() => {});
            reader.releaseLock();
            await flush();
            await this.index.put(entry);
          }
          if (entry.bytesWritten !== p.size) throw new CacheError('network', 'Download ended early');
        } catch (error) {
          await this.index.put(entry);
          const failure = normalize(error, p.signal);
          if (failure.kind !== 'network' || attempt === 4) throw failure;
          await backoff(250 * 2 ** attempt, p.signal);
        }
      }
      aborted(p.signal);
      await this.checkHash(entry, p.signal);
      entry.state = 'complete';
      entry.verifiedAt = Date.now();
      entry.lastUsedAt = Date.now();
      await this.index.put(entry);
      return entry;
    } catch (error) {
      const failure = normalize(error, p.signal);
      if (failure.kind === 'integrity') { await this.store.remove(p.sha256); await this.index.remove(p.sha256); }
      throw failure;
    }
  }

  private async checkHash(entry: CacheEntry, signal?: AbortSignal): Promise<void> {
    const hash = await createSHA256();
    hash.init();
    let size = 0;
    for await (const chunk of this.store.stream(entry.sha256)) {
      aborted(signal);
      size += chunk.length;
      hash.update(chunk);
    }
    aborted(signal);
    if (size !== entry.size || hash.digest('hex') !== entry.sha256) throw new CacheError('integrity');
  }

  async read(sha256: string): Promise<Blob> {
    if (!(await this.has(sha256))) throw new CacheError('permanent', 'Game is not complete');
    const blob = await this.store.read(sha256);
    await this.touch(sha256);
    return blob;
  }
  async remove(sha256: string): Promise<void> {
    if (active.has(sha256)) throw new CacheError('permanent', 'Game download is active');
    await this.store.remove(sha256);
    await this.index.remove(sha256);
  }
  async setPinned(sha256: string, pinned: boolean): Promise<void> {
    const entry = await this.index.update(sha256, entry => { entry.pinned = pinned; });
    if (!entry) throw new CacheError('permanent', 'Game is not cached');
  }
  async requestPersistence(): Promise<boolean> { return (await storage()?.persist?.()) ?? false; }
  async usage(): Promise<{ used: number; quota: number | null; persisted: boolean; entries: number }> {
    const entries = await this.list();
    const manager = storage();
    const estimate = manager?.estimate ? await manager.estimate() : {};
    return { used: entries.reduce((sum, entry) => sum + entry.bytesWritten, 0), quota: estimate.quota ?? null,
      persisted: manager?.persisted ? await manager.persisted() : false, entries: entries.length };
  }
  async evict(bytesNeeded: number, opts: { protect?: string[] } = {}): Promise<number> {
    const cutoff = Date.now() - 7 * DAY;
    const candidates = (await this.list()).filter(e => !e.pinned && !active.has(e.sha256) && !opts.protect?.includes(e.sha256)
      && (e.state === 'complete' || e.lastUsedAt < cutoff));
    candidates.sort((a, b) => Number(a.state === 'complete') - Number(b.state === 'complete') || a.lastUsedAt - b.lastUsedAt);
    let freed = 0;
    for (const entry of candidates) {
      if (freed >= bytesNeeded) break;
      await this.remove(entry.sha256);
      freed += entry.bytesWritten;
    }
    return freed;
  }
  async autoClean(policy: { maxBytes?: number; maxAgeDays?: number }): Promise<void> {
    let entries = await this.list();
    if (policy.maxAgeDays !== undefined) {
      const cutoff = Date.now() - policy.maxAgeDays * DAY;
      for (const entry of entries) if (!entry.pinned && !active.has(entry.sha256) && entry.lastUsedAt < cutoff) await this.remove(entry.sha256);
      entries = await this.list();
    }
    if (policy.maxBytes !== undefined) {
      let used = entries.reduce((sum, e) => sum + e.bytesWritten, 0);
      for (const entry of entries.sort((a, b) => a.lastUsedAt - b.lastUsedAt)) {
        if (used <= policy.maxBytes) break;
        if (entry.pinned || active.has(entry.sha256)) continue;
        await this.remove(entry.sha256);
        used -= entry.bytesWritten;
      }
    }
  }
  async verify(sha256: string): Promise<CacheEntry> {
    const entry = await this.get(sha256);
    if (!entry || entry.state !== 'complete') throw new CacheError('permanent', 'Game is not complete');
    try { await this.checkHash(entry); }
    catch (error) {
      await this.remove(sha256);
      throw new CacheError('integrity', 'Cached game is corrupt or missing', { cause: error });
    }
    entry.verifiedAt = Date.now();
    await this.index.put(entry);
    return entry;
  }
}
